import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "@/lib/orderApi";
import { MIN_QTY, roundQty } from "@/lib/qty";

// docs/frontend-billing-contracts.md §1: the preview is debounced at 320ms, and the
// discount *reason* must never retrigger it.
const PREVIEW_DEBOUNCE_MS = 320;

const TERMINAL = new Set(["closed", "cancelled"]);

/** The offending field out of a FastAPI 422 `loc`: ["body", 0, "quantity"] → "quantity". */
const invalidField = entry => {
  const parts = (Array.isArray(entry?.loc) ? entry.loc : []).filter(
    part => typeof part === "string" && part !== "body" && part !== "query" && part !== "path"
  );
  return parts.length ? parts[parts.length - 1].replaceAll("_", " ") : null;
};

/**
 * A 422 is a contract mismatch between this terminal and the server it is talking
 * to, and Pydantic's own words are written for whoever wrote the schema: a captain
 * tapping ½ was shown "Input should be a valid integer, got a number with a
 * fractional part" in the order panel. That is not something a captain can act on,
 * and it names an internal field as if it were their mistake.
 *
 * Business rules keep their server text — a 409 (version conflict), a 423 (billing
 * freeze) and a 400 (bad transition) all carry a message written for the operator.
 * Only the validation shape is translated.
 */
const validationMessage = detail => {
  const fields = [...new Set(detail.map(invalidField).filter(Boolean))];
  const what = fields.length ? `the ${fields.join(" and ")}` : "something";
  return `This terminal sent ${what} in a form the server will not accept — it is running a different build. Reload; if it keeps happening the server needs updating.`;
};

const messageFor = error => {
  if (error?.response?.status === 409) {
    return "This order changed on another terminal. Reloaded — please redo that step.";
  }
  const detail = error?.response?.data?.detail;
  if (error?.response?.status === 422 && Array.isArray(detail) && detail.length) {
    return validationMessage(detail);
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) return detail[0]?.msg || "Request rejected";
  return error?.message || "Could not reach the server";
};

/**
 * Run an additive mutation, and if another terminal moved the order on first
 * (409, stale version), re-read the version and run it once more.
 *
 * Adding a line or firing a KOT does not depend on what the other terminal
 * changed — a 409 there only means our version number is old. Bouncing it back
 * as "changed on another terminal, redo that step" made two people working one
 * table fight each other all night (owner, 2026-10-23). A 409 rejects the first
 * attempt outright, so the retry cannot double-punch. Edits that DO depend on
 * what changed (voids, discounts, the bill) keep the explicit refusal.
 */
const withFreshVersion = async (orderId, version, token, run) => {
  try {
    return await run(version);
  } catch (error) {
    if (error?.response?.status !== 409) throw error;
    const current = await api.getOrder(orderId, token);
    return await run(current.version);
  }
};

/**
 * Owns the current order. Every mutation goes through the REST API so the totals
 * come from the same billing service that prints the bill — the page's old local
 * arithmetic ignored the GST split, service charge, round-off and loyalty.
 */
export function useOrder(token, onOrderChanged) {
  const [order, setOrder] = useState(null);
  const [preview, setPreview] = useState(null);
  const [orderError, setOrderError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [discount, setDiscountState] = useState({ discount: 0, discountPct: null });
  // Deliberately NOT part of `discount`: the preview effect depends on that object's
  // identity, and docs/frontend-billing-contracts.md §1 requires that typing a
  // reason never retriggers the debounced preview. It only travels on generate.
  const [discountReason, setDiscountReason] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const previewTimer = useRef(null);
  // Debouncing collapses a burst into one call but does not serialise the calls it
  // does make: punch A, then B 400ms later, and if A's preview resolves after B's
  // the cart reverts to the pre-B total. That number is the "Amount due", the
  // "Settle · ₹" button and the figure the split form divides — so a stale response
  // is money on screen. Every response carries the id of the request that asked for
  // it; anything but the newest is dropped.
  const previewSeq = useRef(0);
  // Mirrors order.id so realtime handlers can test it without taking `order` as a
  // dependency — that would give every handler a new identity on each punch.
  const currentId = useRef(null);

  // A device that changes an order must see that change on its OWN floor at once.
  // Every table grid (useActiveOrders) refreshes only on an `order_update`, and
  // until 2026-09-17 the WebSocket was the only thing that ever produced one. So the
  // phone that had just settled a table kept showing it occupied until realtime
  // caught up — reported from the floor as "tables are not freed after bill
  // settlement", and before that as having to refresh "even that same phone" after
  // punching. The server was right the whole time: the table was free in the DB.
  //
  // Called only after the server has accepted the mutation. The listener runs in a
  // try/catch deliberately: a throw here would reach handleFailure and tell the
  // cashier a settled bill "is still open", inviting a second settle of a closed
  // order. It is logged, not swallowed.
  const announceRef = useRef(onOrderChanged);
  announceRef.current = onOrderChanged;
  const announce = useCallback(orderId => {
    try {
      announceRef.current?.(orderId);
    } catch (listenerError) {
      console.error("Floor refresh after an order change failed", listenerError);
    }
  }, []);

  const clearError = useCallback(() => setOrderError(null), []);

  const refresh = useCallback(
    async orderId => {
      const [fresh, items] = await Promise.all([
        api.getOrder(orderId, token),
        api.getOrderItems(orderId, token).catch(() => []),
      ]);
      const next = {
        id: fresh.id,
        version: fresh.version,
        status: fresh.status,
        guestCount: fresh.guest_count,
        orderType: fresh.order_type,
        tableId: fresh.table_id ?? null,
        orderNumber: fresh.order_number,
        notes: fresh.notes ?? "",
        // The server's own figure for this order. Only meaningful once the bill is
        // frozen — settleOrder reports it back when it settles an already-billed
        // order, where there is no fresh bill response to quote instead.
        total: fresh.total ?? null,
        items,
      };
      setOrder(next);
      currentId.current = next.id;
      return next;
    },
    [token]
  );

  /**
   * Re-read the open order if a realtime event names it.
   *
   * A captain adding an item on a tablet bumps `version`; without this the till
   * holding the same order keeps a stale version and the next mutation 409s, which
   * the operator experiences as the bill randomly refusing to save.
   */
  const refreshIfCurrent = useCallback(
    async orderId => {
      if (!currentId.current || currentId.current !== orderId) return;
      try {
        await refresh(orderId);
      } catch {
        /* transient; the next user action surfaces any real problem */
      }
    },
    [refresh]
  );

  const handleFailure = useCallback(
    async (error, orderId) => {
      setOrderError(messageFor(error));
      // On a conflict the local version is stale; refetch so the next attempt uses
      // the current one rather than failing again.
      if (error?.response?.status === 409 && orderId) {
        try {
          await refresh(orderId);
        } catch {
          /* leave the order as-is; the message already told the user */
        }
      }
    },
    [refresh]
  );

  const openOrder = useCallback(
    async (table, orderType = "dine_in", guestCount = 2) => {
      setBusy(true);
      setOrderError(null);
      try {
        if (table) {
          // idx_one_active_order_per_table is enforced by the database: creating a
          // second active order for a table fails at insert, so resume instead.
          const active = await api.listActiveOrders(token).catch(() => []);
          // By table id: numbers repeat across areas.
          const running = active.find(
            row => row.table_id === table.id && !TERMINAL.has(row.status)
          );
          if (running) return await refresh(running.id);
        }
        const created = await api.createOrder(
          { tableId: table ? table.id : null, guestCount, orderType },
          token
        );
        announce(created.id);
        return await refresh(created.id);
      } catch (error) {
        await handleFailure(error);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, handleFailure, refresh, token]
  );

  const startOrder = useCallback(
    async (payload) => {
      setBusy(true);
      setOrderError(null);
      try {
        const result = await api.startOrder(payload, token);
        announce(result.order.id);
        return await refresh(result.order.id);
      } catch (error) {
        if (error?.response?.status === 409) {
          throw error;
        }
        await handleFailure(error);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, handleFailure, refresh, token]
  );

  /**
   * Punch a line.
   *
   * `modifierIds` and `notes` were hardcoded to `[]` and `null` here, so the two
   * fields the API has always accepted could not be reached from any caller: a
   * "no onion" or an extra-cheese modifier had nowhere to travel. They are
   * parameters now, defaulted to the old values so every existing call site is
   * unchanged.
   */
  const punchItem = useCallback(
    async (menuItem, variant, quantity = 1, modifierIds = [], notes = null) => {
      if (!order) {
        setOrderError("Pick a table before punching items.");
        return null;
      }
      // Half plates are punched all day, so the floor is the server's 0.25 rather
      // than a whole plate. Snapped to 2dp first: a stepper that subtracted its way
      // to 0.30000000000000004 would be rejected by the schema, not by this guard.
      const qty = roundQty(quantity);
      if (qty < MIN_QTY) return order;
      setBusy(true);
      setOrderError(null);
      try {
        // bootstrap_service serialises menu ids as str(item.id); the API takes int.
        // Quantity travels in the single call: looping punchItem would fire N
        // requests each bumping `version`, and the later ones would 409.
        const line = {
          menu_item_id: Number(menuItem.id),
          quantity: qty,
          // An empty string is not a note; the server stores it as one.
          notes: notes?.trim?.() ? notes.trim() : null,
          modifier_ids: (modifierIds || []).map(Number),
          variation_id: variant ? Number(variant.id) : null,
        };
        await withFreshVersion(order.id, order.version, token, v => api.addItems(order.id, [line], v, token));
        announce(order.id);
        return await refresh(order.id);
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, handleFailure, order, refresh, token]
  );

  const clearOrder = useCallback(() => {
    setOrder(null);
    currentId.current = null;
    // Invalidate any preview still in flight. settleOrder clears the till the moment
    // the bill closes; without this the response for the order just settled lands
    // afterwards and paints "Amount due ₹X" beside "No open order".
    previewSeq.current += 1;
    setPreview(null);
    setDiscountState({ discount: 0, discountPct: null });
    setDiscountReason(null);
    setRedeemPoints(0);
    setOrderError(null);
  }, []);

  /**
   * Void lines on the open order.
   *
   * The cart's delete and quantity-down controls used to toast "needs the cancel-items
   * flow" — an instruction pointing at a flow that was never built, so an accidental
   * punch could not be corrected at all. The endpoint existed the whole time; nothing
   * called it. Lines already fired also get a VOID slip at their station.
   */
  const cancelItems = useCallback(
    async (itemIds, reasonCode = null) => {
      if (!order) {
        setOrderError("No open order to void from.");
        return null;
      }
      if (!itemIds?.length) return order;
      setBusy(true);
      setOrderError(null);
      try {
        const response = await api.cancelItems(order.id, itemIds, reasonCode, order.version, token);
        announce(order.id);
        // The last live line of a fired order went: the server ended the order and
        // freed its table (bible §39.2 "Fully cancelled"). Nothing is left to show.
        if (response?.order_cancelled) {
          clearOrder();
          return { ...order, status: "cancelled", items: [], orderCancelled: true, tableFreed: !!response.table_freed };
        }
        return await refresh(order.id);
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, clearOrder, handleFailure, order, refresh, token]
  );

  const setDiscount = useCallback(next => setDiscountState(next), []);

  /**
   * Split selected lines onto a sibling bill for the same table.
   *
   * The modal used to queue a BILLING_SPLIT action, which the dispatcher recorded
   * in audit_log and nothing else — the guests got one bill and the screen said
   * they had two. Returns the new order so the caller can name it.
   */
  const splitItems = useCallback(
    async itemIds => {
      if (!order) {
        setOrderError("No open order to split.");
        return null;
      }
      if (!itemIds?.length) {
        setOrderError("Pick at least one item to move onto the new bill.");
        return null;
      }
      setBusy(true);
      setOrderError(null);
      try {
        const created = await api.splitOrder(order.id, itemIds, order.version, token);
        announce(order.id);
        await refresh(order.id);
        return created;
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, handleFailure, order, refresh, token]
  );

  /** Fold another running order into this one. The source is cancelled, not deleted. */
  const mergeFrom = useCallback(
    async (sourceOrderId, sourceVersion) => {
      if (!order) {
        setOrderError("No open order to merge into.");
        return null;
      }
      setBusy(true);
      setOrderError(null);
      try {
        await api.mergeOrders(order.id, sourceOrderId, order.version, token, sourceVersion);
        announce(order.id);
        return await refresh(order.id);
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, handleFailure, order, refresh, token]
  );

  /**
   * Comp the whole bill, or make it no-charge.
   *
   * `authorizedBy` is the user_id POST /auth/authorize returned for the manager's
   * PIN. The server re-verifies it: a client that could assert who approved could
   * comp any bill by naming a manager id, which is readable from /api/users.
   */
  const compOrder = useCallback(
    async ({ scope, reason, authorizedBy }) => {
      if (!order) {
        setOrderError("No open order to comp.");
        return null;
      }
      setBusy(true);
      setOrderError(null);
      try {
        await api.markComplimentary(
          order.id,
          { scope, reason, authorizedBy },
          order.version,
          token
        );
        announce(order.id);
        return await refresh(order.id);
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, handleFailure, order, refresh, token]
  );

  /** Set covers, and optionally attach a customer, on the open order. */
  const setGuestDetails = useCallback(
    async ({ guestCount, customerId }) => {
      if (!order) {
        setOrderError("No open order to update.");
        return null;
      }
      setBusy(true);
      setOrderError(null);
      try {
        if (guestCount !== undefined && guestCount !== null) {
          await api.updateOrder(order.id, { guest_count: guestCount }, order.version, token);
        }
        // Linking takes no If-Match, so it must follow the patch above rather than
        // race it — the patch bumps the version this call would otherwise carry.
        if (customerId) await api.linkOrderCustomer(order.id, customerId, token);
        announce(order.id);
        return await refresh(order.id);
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, handleFailure, order, refresh, token]
  );

  /**
   * Persist an order note.
   *
   * Previously this queued an ADD_ORDER_NOTE action, which the sync dispatcher only
   * writes to audit_log — so the note never reached `orders.notes` and never printed.
   * PATCH /orders/{id} is the endpoint that actually stores it.
   */
  const setOrderNote = useCallback(
    async note => {
      if (!order) {
        setOrderError("No open order to annotate.");
        return null;
      }
      setBusy(true);
      setOrderError(null);
      try {
        await api.updateOrder(order.id, { notes: note }, order.version, token);
        return await refresh(order.id);
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [handleFailure, order, refresh, token]
  );

  // Named distinctly from useKotActions.sendKot(table, cart, captain): both are
  // spread onto the same context, and a shared name silently shadows one of them.
  const fireOrderKot = useCallback(async () => {
    if (!order) {
      setOrderError("No open order to send.");
      return null;
    }
    setBusy(true);
    setOrderError(null);
    try {
      const result = await withFreshVersion(order.id, order.version, token, v => api.fireKot(order.id, v, token));
      announce(order.id);
      await refresh(order.id);
      return result;
    } catch (error) {
      await handleFailure(error, order.id);
      return null;
    } finally {
      setBusy(false);
    }
  }, [announce, handleFailure, order, refresh, token]);

  /**
   * Park the order and clear the till.
   *
   * There is no `hold` state in the order FSM and adding one would mean a new enum
   * value plus a transition row for every path that touches it. A held bill is just
   * an order that stays open while the cashier serves someone else — it is already
   * on the running-orders board, so releasing the screen is the whole operation.
   */
  const holdOrder = useCallback(() => {
    const held = order;
    clearOrder();
    return held;
  }, [clearOrder, order]);

  /**
   * Void the whole order and give the table back.
   *
   * Distinct from `cancelItems` in the one way that matters operationally: voiding
   * the lines one by one leaves the order live, and the database's
   * `idx_one_active_order_per_table` then refuses every new order on that table for
   * the rest of service. Only this call releases the table, the advisory lock and
   * the kitchen display together.
   *
   * The till lets go of the order on success — it is terminal, and leaving it on
   * screen would offer Settle and Print KOT for a bill that no longer exists.
   * Declared after `clearOrder` because it names it as a dependency: a `const` read
   * in a dependency array before its own declaration is a TDZ error, not a hoist.
   */
  const cancelOrder = useCallback(
    async ({ reasonCode = null, reason = null, authorizedBy = null } = {}) => {
      if (!order) {
        setOrderError("No open order to cancel.");
        return null;
      }
      setBusy(true);
      setOrderError(null);
      try {
        const result = await api.cancelOrder(
          order.id,
          { reasonCode, reason, authorizedBy },
          order.version,
          token
        );
        announce(order.id);
        clearOrder();
        return result;
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, clearOrder, handleFailure, order, token]
  );

  /**
   * Actually settle the bill: generate it, then close it with the tender.
   *
   * The previous version queued a SETTLE_BILL action, cleared the cart and toasted
   * success — but the sync dispatcher only writes an audit line, so the order was
   * never closed or paid server-side while the UI insisted it was. Returns null on
   * failure and leaves the order open so the operator can retry.
   *
   * An order whose bill is ALREADY printed is settled, never re-billed. There is no
   * `billing → billing` row in `order_transitions` (migration 009 seeds only
   * billing → closed), so POST /orders/{id}/bill on such an order is a 400 — which
   * is exactly what stranded the captain who printed the bill first and then came
   * back to take the money. The bill is frozen at that point anyway: its discount,
   * redemption and round-off are already in the document, and re-sending this
   * terminal's `discount` state would be arguing with it. The total quoted back is
   * the server's own `orders.total` for the frozen bill, not a locally computed one.
   */
  const settleOrder = useCallback(
    async (paymentMethod, payments = null) => {
      if (!order) {
        setOrderError("No open order to settle.");
        return null;
      }
      setBusy(true);
      setOrderError(null);
      try {
        const alreadyBilled = order.status === "billing";
        let bill = null;
        if (alreadyBilled) {
          // Re-read anyway: the version in hand may predate whatever printed the
          // bill, and close carries If-Match.
          const current = await refresh(order.id);
          await api.closeOrderOnServer(order.id, paymentMethod, current.version, token, payments);
          bill = { total: current.total, alreadyBilled: true };
        } else {
          bill = await api.generateBill(
            order.id,
            { ...discount, discountReason, redeemPoints },
            order.version,
            token
          );
          // The bill bumps the version; re-read before closing or the close 409s.
          const afterBill = await refresh(order.id);
          await api.closeOrderOnServer(order.id, paymentMethod, afterBill.version, token, payments);
        }
        announce(order.id);
        clearOrder();
        return bill;
      } catch (error) {
        await handleFailure(error, order.id);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [announce, clearOrder, discount, discountReason, handleFailure, order, redeemPoints, refresh, token]
  );

  // Authoritative totals. Debounced so a burst of punches or discount keystrokes
  // makes one call, matching the contract the previous frontend enforced.
  useEffect(() => {
    // Cancelled rows STAY in order_items — GET /orders/{id}/items returns them —
    // so counting `order.items` treats a fully-voided order as a cart with lines.
    // The guard then did not fire, the last good preview survived, and the cashier
    // was shown a real rupee "Amount due" behind a live Settle button for a bill
    // the server would only ever answer with "No items in order".
    const live = (order?.items || []).filter(item => item.status !== "cancelled");
    if (!order?.id || !live.length) {
      // An emptied cart must not be re-totalled by a request made while it still had
      // lines, so the in-flight id is retired here too.
      previewSeq.current += 1;
      setPreview(null);
      return undefined;
    }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    const orderId = order.id;
    previewTimer.current = setTimeout(() => {
      const seq = (previewSeq.current += 1);
      api
        .previewBill(orderId, { ...discount, redeemPoints }, token)
        .then(result => {
          if (previewSeq.current !== seq) return;
          setPreview(result);
        })
        .catch(error => {
          if (previewSeq.current !== seq) return;
          // The failed total must go with the failure. Leaving the previous
          // preview standing is what kept a stale "Amount due" on screen — and
          // the Settle button is enabled by `preview` being truthy, so the
          // cashier could press it against a figure the server had just refused.
          setPreview(null);
          setOrderError(messageFor(error));
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(previewTimer.current);
  }, [order?.id, order?.items, order?.version, discount, redeemPoints, token]);

  return {
    order,
    preview,
    orderError,
    orderBusy: busy,
    discount,
    discountReason,
    redeemPoints,
    openOrder,
    startOrder,
    punchItem,
    fireOrderKot,
    setDiscount,
    setDiscountReason,
    setRedeemPoints,
    settleOrder,
    cancelItems,
    cancelOrder,
    setOrderNote,
    splitItems,
    mergeFrom,
    compOrder,
    setGuestDetails,
    holdOrder,
    clearOrder,
    refreshIfCurrent,
    clearOrderError: clearError,
  };
}
